<template>
    <CModal
        :title="titleComponent"
        :show="showComponent"
        :color="colorComponent"
        :size="sizeComponent"
    >
        <CForm
            ref="formUploadSimpleFilterFile"
            @submit="formSubmit($event, 'uploadDeliveriesFile')"
        >
            <CContainer class="mb-3 mt-2">
                <CRow>
                    <CCol>
                        <p>Select a file to upload:</p>
                    </CCol>
                </CRow>
                <CRow>
                    <CCol sm="12">
                        <div class="d-flex justify-content-center">
                            <CCButtonUploadFile
                                ref="inputUploadFile"
                                uploadType="delivery-campaigns-deliveries"
                                @change="uploadFile"
                            >
                            </CCButtonUploadFile>
                        </div>
                    </CCol>
                </CRow>
                <CRow>
                    <CCol sm="12">
                        <CSelect
                            :value.sync="forms.uploadDeliveriesFile.delimiter"
                            :options="[
                                {
                                    value: ',',
                                    label: ',',
                                },
                                {
                                    value: ';',
                                    label: ';',
                                },
                            ]"
                            :label="'Delimiter'"
                            :horizontal="true"
                            placeholder="Select a delimiter"
                            size="sm"
                        ></CSelect>
                    </CCol>
                </CRow>

                <CRow>
                    <CCol sm="12">
                        <CCTable
                            :items="table.items"
                            :fields="table.fields"
                            :resizable="false"
                        >
                            <template #file_fields="{ item }">
                                <SelectAsync
                                    :options="headers"
                                    :value.sync="item.file_fields"
                                    :placeholder="$t('Select a field to map')"
                                    size="sm"
                                />
                            </template>
                            <template #default_value="{ item }">
                                <CInput
                                    :value.sync="item.default_value"
                                    :placeholder="$t('Enter the key')"
                                    :invalidFeedback="$t('Key is required')"
                                    class="mb-0"
                                    size="sm"
                                />
                            </template>
                        </CCTable>
                    </CCol>
                </CRow>
            </CContainer>

            <ButtonLoading
                type="submit"
                :block="true"
                color="primary"
                :loading="forms.uploadDeliveriesFile._loading"
            >
                <span>{{ $t("upload") }}</span>
            </ButtonLoading>
        </CForm>
        <template #footer>
            <CButton color="secondary" @click="closeModal()">
                {{ $t("cancel") }}
            </CButton>
            <ButtonLoading
                color="primary"
                @click="executeSubmitForm"
                :loading="forms.uploadDeliveriesFile._loading"
            >
                {{ $t("upload") }}
            </ButtonLoading>
        </template>
    </CModal>
</template>

<script>
import formMixin from "~/mixins/form";
import modalComponentMixin from "~/mixins/modalComponent";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import ButtonLoading from "~/components/ButtonLoading.vue";
import CCButtonUploadFile from "~/components/CCButtonUploadFile";
import SelectAsync from "~/components/elements/SelectAsync";
import CCTable from "~/components/CCTable";
import { CRow } from "@coreui/vue";

export default {
    name: "CCModalUploadDeliveriesFile",
    mixins: [formMixin, validationMixin, modalComponentMixin],
    components: {
        ButtonLoading,
        CCButtonUploadFile,
        CCTable,
        SelectAsync,
    },
    data() {
        return {
            headers: [],
            firstLine: null,
            forms: {
                uploadDeliveriesFile: {
                    delimiter: null,
                    _loading: false,
                },
            },
            table: {
                items: [
                    {
                        allowed_fields: "lead_id",
                        file_fields: null,
                        default_value: null,
                    },
                    {
                        allowed_fields: "status",
                        file_fields: null,
                        default_value: null,
                    },
                    {
                        allowed_fields: "sent_at",
                        file_fields: null,
                        default_value: null,
                    },
                ],
                fields: [
                    {
                        key: "allowed_fields",
                        label: "Allowed fields",
                    },
                    {
                        key: "file_fields",
                        label: "File fields",
                    },
                    {
                        key: "default_value",
                        label: "Default value",
                    },
                ],
            },
        };
    },

    props: {
        deliveryCampaignId: {
            type: Number | String,
            default: null,
        },
        size: {
            type: String,
            default: "lg",
        },
    },

    validations: {
        forms: {
            uploadDeliveriesFile: {
                name: {
                    required,
                },
            },
        },
    },

    watch: {
        show() {
            this.showComponent = this.show;
            if (!this.show) {
                this.resetForm("uploadDeliveriesFile");
                const refInputUploadFile = this.$refs["inputUploadFile"];
                if (refInputUploadFile) {
                    refInputUploadFile.reset();
                }
                this.table.items = this.table.items.map((item) => {
                    return {
                        ...item,
                        file_fields: null,
                        default_value: null,
                    };
                });
                this.$v.$reset();
            }
        },
        "forms.uploadDeliveriesFile.delimiter"(newValue, oldValue) {
            if (this.firstLine) {
                this.headers = this.firstLine.split(newValue);
            }
        },
    },

    methods: {
        async uploadFile(e) {
            this.firstLine = e.firstLine;
            this.headers = this.firstLine.split(
                this.forms.uploadDeliveriesFile.delimiter
            );
        },
        async formSubmit(e, formName) {
            e.preventDefault();
            try {
                this.forms.uploadDeliveriesFile._loading = true;
                const refInputUploadFile = this.$refs["inputUploadFile"];
                const { key, fileName } = await refInputUploadFile.saveFile();

                let mappingFields = {};
                let defaultValues = {};
                for (let i = 0; i < this.table.items.length; i++) {
                    const item = this.table.items[i];

                    mappingFields = {
                        ...mappingFields,
                        [item.allowed_fields]: item.file_fields
                            ? item.file_fields
                            : null,
                    };

                    defaultValues = {
                        ...defaultValues,
                        [item.allowed_fields]: item.default_value
                            ? item.default_value
                            : null,
                    };
                }

                await this.fetcher({
                    baseURL: process.env.VUE_APP_API_SASS,
                    // baseURL: "http://localhost:3000/prod",
                    url: `/imports/delivery-campaigns-deliveries/${key}/process`,
                    method: "POST",
                    data: {
                        fileName,
                        deliveryCampaignId: this.deliveryCampaignId,
                        delimiter: this.forms.uploadDeliveriesFile.delimiter,
                        key,
                        allowedFieldsMapping: mappingFields,
                        allowedFieldsDefaultValues: defaultValues,
                    },
                });

                this.$emit("uploadedFile", { key, fileName });
                this.showComponent = false;
                this.showMessageSuccess();
            } catch (error) {
                this.showMessageError(error);
            } finally {
                this.forms.uploadDeliveriesFile._loading = false;
            }

            this.$emit("submit", formName);
        },

        executeSubmitForm() {
            this.$refs.formAddLang.requestSubmit();
        },
    },
};
</script>

<style></style>
